import * as Yup from 'yup';

export const gridSizes = {
  details: { xs: 12, md: 6, lg: 3 },
  rules: { xs: 12, md: 4, lg: 4 },
  levels:{ xs: 12, md: 6, lg: 3 }
} as const;

export interface PackageLevelItem {
  id?: number;
  level_code: string;
  level_name: string;
}


export interface PackageRuleItem {
  id?: number;
  rule_code: 'CONCESSION' | 'DOCTOR_SHARE' | 'RATE_EDIT' | string;
  rule_name: string;
  rule_description: string;
  active: boolean;
  min_percent?: string | null; 
  max_percent?: string | null;
  percentage?: string | null; 
  amount?: string | null;
  min_amount?: string | null;
  max_amount?: string | null; 
}


export interface PackageClassRateItem {
  id?: number;
  class_name: string;
  rate: string;
  enabled?: boolean;
}

// This is the shape used in the form (for UI display and selection)
// `id` here is the tariff master id (from /tariff/ API)
export interface PackageTariffItem {
  id: number;            // Tariff master ID (from tariff API)
  label: string;
  name: string;
  code: string;
  description?: string;
  tariff_name?: string;
  tariff_code?: string;
  tariff?: number;        // For backward compatibility, same as id
  // These are set when tariff comes from package GET API (existing relation)
  package_tariff_id?: number;  // The relation ID from package GET API (stored as `id` in response)
}

// This is the shape returned by Package GET API for tariffs
// id = package-tariff relation ID, tariff = tariff master ID
export interface sourceTariffItem {
  id: number;            // Package-tariff relation ID
  tariff: number;        // Tariff master ID
  tariff_id: number;     // Same as id (relation ID) - kept for backward compat
  tariff_code: string;
  tariff_name: string;
}

export interface PackageDetailsValues {
  id?: number | null;
  code?: string | number;
  code_type?: string;
  code_details?: string;
  sac_code?: string;
  clinic?: number;
  package_name?: string;
  short_description?: string;
  long_description?: string;
  package_amount?: string | null;
  effective_date?: string; 
  expiry_date?: string; 
  status?: boolean | null;
  freeze?: boolean | null;
  levels?: PackageLevelItem[];
  //class_rates?: PackageClassRateItem[];
  rules?: PackageRuleItem[];
  tariffs?: PackageTariffItem[];
}

// Final payload shape sent to API
export interface PackagePayload {
  // code_type: string;
  // code_details: string;
  // sac_code: string | number;
  clinic: number | null;
  package_name: string;
  short_description?: string;
  long_description?: string;
  package_amount?: number | null;
  effective_date: string;
  expiry_date: string;
  status: boolean | null | undefined;
  freeze: boolean | null | undefined;
  levels: Array<{
    level_code: string;
  }>;
  // class_rates: Array<{
  //   class_name: string;
  //   rate: number;
  // }>;
  rules: Array<{
    rule_code: string;
    active: boolean;
    min_percent?: number;
    max_percent?: number;
    percentage?: number;
    amount?: number;
    min_amount?: number;
    max_amount?: number;
  }>;
  tariffs: Array<{
    tariff: number;
  }>;
}


export interface MasterDataItem {
  id: number;
  name: string;
  code: string;
  translation: string | null;
}

export interface MasterDataResponse {
  SAC_CODE: MasterDataItem[];
  CODE_TYPE: MasterDataItem[];
  TAX_TYPE: MasterDataItem[];
  packagerulesmaster: MasterDataItem[];
  level_1: MasterDataItem[];
  level_2: MasterDataItem[];
  level_3: MasterDataItem[];
  level_4: MasterDataItem[];
}
export interface PackageClassMasterItem {
id: number;
name: string;
code: string;
description: string;
status: boolean;
}

export interface PackageClassMasterResponse {
success: boolean;
data: PackageClassMasterItem[];
}


export interface tariffListItem {
  id: number;
  name: string;
  code: string;
  description: string;
  start_date: string;
  end_date: string | null;
  status: boolean;
}

export interface tariffListResponse {
  success: boolean;
  data: tariffListItem[];
}
export interface tarriffRateItem {
  id?: number;
  tariff_id: number;
  package_class_id: number | null;
  rate: string;
  class_name?: string;
  tariff_name?: string;
}

// Tax master interfaces
export interface TaxItem {
  id: number;
  name: string;
  code: string;
  rate: string; // percentage as string from API, e.g., "18.00"
  description?: string;
  translation?: Record<string, unknown>;
}

export interface TaxListResponse {
  success: boolean;
  data: TaxItem[];
}



export const initialValues: PackageDetailsValues = {
  code_type: '',
  code_details: '',
  sac_code: '',
  clinic: undefined,
  package_name: '',
  short_description: '',
  long_description: '',
  effective_date: '',
  expiry_date: '',
  package_amount: null,
  status: true,
  freeze: false,
  levels: [],
  //class_rates: [],
  rules: []

};

export const validationSchema = Yup.object({
  //code_type: Yup.string().required('Code Type is required'),
  //code_details: Yup.string().required('Code Details is required'),
  //sac_code: Yup.string().required('SAC Code is required'),
  package_name: Yup.string().required('Package Name is required'),
  short_description: Yup.string(),
  long_description: Yup.string(),
  effective_date: Yup.string().required('Effective Date is required'),
  expiry_date: Yup.string()
    .required('Expiry Date is required')
    .test('date-order', 'Expiry must be after Effective Date', function (value) {
      const eff = new Date(this.parent.effective_date);
      const exp = new Date(value);
      if (!value || !this.parent.effective_date) return true;
      return exp.getTime() >= eff.getTime();
    }),
  package_amount: Yup.number()
    .required('Package Amount is required')
    .min(0, 'Package Amount cannot be negative'),
  status: Yup.boolean().nullable(),
  freeze: Yup.boolean().nullable(),
  levels: Yup.array().of(
    Yup.object({
      level_code: Yup.string().required(),
    })
  ),
  class_rates: Yup.array().of(
    Yup.object({
      class_name: Yup.string().required(),
      enabled: Yup.boolean().optional(),
      rate: Yup.string().when('enabled', {
        is: true,
        then: (schema) => schema.required('Rate is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  ),
  rules: Yup.array().of(
    Yup.object({
      rule_code: Yup.string().required(),
      rule_name: Yup.string().required(),
      rule_description: Yup.string().required(),
      active: Yup.boolean().required(),
      min_percent: Yup.string()
        .nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'CONCESSION' && active === true,
          then: (schema) => schema
            .required('Min. (%) is required')
            .test('min-range', 'Min. (%) must be between 0 and 100', function(value) {
              if (!value) return true;
              const num = Number(value);
              return num >= 0 && num <= 100;
            })
            .test('min-less-than-max', 'Min. (%) must be less than Max. (%)', function(value) {
              const max = this.parent.max_percent;
              if (!value || !max) return true;
              return Number(value) < Number(max);
            }),
        }),
      max_percent: Yup.string()
        .nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'CONCESSION' && active === true,
          then: (schema) => schema
            .required('Max. (%) is required')
            .test('max-range', 'Max. (%) must be between 0 and 100', function(value) {
              if (!value) return true;
              const num = Number(value);
              return num >= 0 && num <= 100;
            })
            .test('max-greater-than-min', 'Max. (%) must be greater than Min. (%)', function(value) {
              const min = this.parent.min_percent;
              if (!value || !min) return true;
              return Number(value) > Number(min);
            }),
        }),
      percentage: Yup.string().nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'DOCTOR_SHARE' && active === true,
          then: (schema) => schema
            .required('Percentage is required')
            .test('percentage-range', 'Percentage must be between 0 and 100', function(value) {
              if (!value) return true;
              const num = Number(value);
              return num >= 0 && num <= 100;
            }),
        }),
      amount: Yup.string()
        .nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'DOCTOR_SHARE' && active === true,
          then: (schema) => schema
            .required('Amount is required')
            .test('amount-non-negative', 'Amount cannot be negative', function(value) {
              if (!value) return true;
              return Number(value) >= 0;
            }),
        }),
      min_amount: Yup.string()
        .nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'RATE_EDIT' && active === true,
          then: (schema) => schema
            .required('Min. Amt. is required')
            .test('min-non-negative', 'Min. Amt. cannot be negative', function(value) {
              if (!value) return true;
              return Number(value) >= 0;
            })
            .test('min-less-than-max', 'Min. Amt. must be less than Max. Amt.', function(value) {
              const max = this.parent.max_amount;
              if (!value || !max) return true;
              return Number(value) < Number(max);
            }),
        }),
      max_amount: Yup.string()
        .nullable()
        .when(['rule_code', 'active'], {
          is: (rule_code: string, active: boolean) => rule_code === 'RATE_EDIT' && active === true,
          then: (schema) => schema
            .required('Max. Amt. is required')
            .test('max-non-negative', 'Max. Amt. cannot be negative', function(value) {
              if (!value) return true;
              return Number(value) >= 0;
            })
            .test('max-greater-than-min', 'Max. Amt. must be greater than Min. Amt.', function(value) {
              const min = this.parent.min_amount;
              if (!value || !min) return true;
              return Number(value) > Number(min);
            }),
        }),
    })
  ),
  tariffs: Yup.array()
    .min(1, 'At least one tariff must be selected')
    .required('Tariffs are required'),

});




// Helper function to detect if tariff is existing (from Package GET API) or new (from Tariff API)
export function isExistingTariff(tariff: sourceTariffItem | PackageTariffItem): tariff is sourceTariffItem {
  // sourceTariffItem has `tariff` property which is the tariff master ID
  return 'tariff' in tariff && typeof (tariff as sourceTariffItem).tariff === 'number';
}

// Helper function to check if a PackageTariffItem has an existing package-tariff relation
export function hasPackageTariffRelation(tariff: PackageTariffItem): boolean {
  return typeof tariff.package_tariff_id === 'number';
}

// Helper function to normalize tariff data (handles both API and form formats)
// Converts sourceTariffItem (from Package GET API) to PackageTariffItem (for form use)
export function normalizeTariff(tariff: PackageTariffItem | sourceTariffItem): PackageTariffItem {
  if (isExistingTariff(tariff)) {
    // From Package GET API: tariff.tariff is the tariff master ID, tariff.id is the relation ID
    return {
      id: tariff.tariff,                    // Use tariff master ID as the form id
      label: tariff.tariff_name,
      name: tariff.tariff_name,
      code: tariff.tariff_code,
      package_tariff_id: tariff.id,         // Store the relation ID for PUT requests
    };
  }
  
  // Already a PackageTariffItem (from Tariff API or already normalized)
  return {
    id: tariff.id,
    label: tariff.label || tariff.name || '',
    name: tariff.name || tariff.label || '',
    code: tariff.code || '',
    description: tariff.description,
    package_tariff_id: tariff.package_tariff_id,  // Preserve if exists
  };
}


// Map Formik values to final API payload
export function buildPackagePayload(values: PackageDetailsValues): PackagePayload {
  return {  
    // code_type: values.code_type || '',
    // code_details: values.code_details || '',
    clinic: values.clinic ?? null,
    // sac_code: values.sac_code || '',
    package_name: values.package_name || '',
    short_description: values.short_description,
    long_description: values.long_description,
    package_amount: values.package_amount ? Number(values.package_amount) : null,
    effective_date: values.effective_date || '',
    expiry_date: values.expiry_date || '',
    status: values.status ?? true,
    freeze: values.freeze ?? false,
    levels: (values.levels || [])
      .filter((lvl) => lvl.level_code)
      .map((lvl) => ({
        id: lvl.id,
        level_code: lvl.level_code,
      })),
    // class_rates: (values.class_rates || [])
    //   .filter((cr) => cr.enabled && cr.class_name && cr.rate !== '')
    //   .map((cr) => ({
    //     id: cr.id,
    //     class_name: cr.class_name,
    //     rate: Number(cr.rate),
    //   })),
    rules: (values.rules || [])
      .filter((r) => r.rule_code && typeof r.active === 'boolean')
      .map((r) => ({
        id: r.id,
        rule_code: r.rule_code,
        active: r.active,
        min_percent: r.min_percent ? Number(r.min_percent) : undefined,
        max_percent: r.max_percent ? Number(r.max_percent) : undefined,
        percentage: r.percentage ? Number(r.percentage) : undefined,
        amount: r.amount ? Number(r.amount) : undefined,
        min_amount: r.min_amount ? Number(r.min_amount) : undefined,
        max_amount: r.max_amount ? Number(r.max_amount) : undefined,
      })),
    tariffs: (values.tariffs || [])
      .map((tariff: PackageTariffItem) => {
        // tariff.id = tariff master ID (to send as `tariff` in payload)
        // tariff.package_tariff_id = relation ID (to send as `id` in PUT for existing relations)
        
        if (hasPackageTariffRelation(tariff)) {
          // Existing tariff relation - include id for PUT
          return {
            id: tariff.package_tariff_id,  // Relation ID
            tariff: tariff.id,              // Tariff master ID
          };
        }
        // New tariff - just send tariff master ID
        return { tariff: tariff.id };
      })
      .filter((t): t is { tariff: number; id?: number } => typeof t.tariff === 'number'),
  };
}



export function getPackageConfigConfigURL(): string {
  return 'billing/billing-config-options/?module=package_configuration';
}

export function getClassMasterURL(): string {
  return 'classes/';
}

export function getPackageDetailsURL(): string {
  return 'packages/';
}

export const toggleStatusURL = (packageId: number): string => {
  return `packages/${packageId}/toggle-status/`;
};

export function getTariffList(): string {
  return 'tariff/';
}
export function assignTariffURL(packageId: number): string {
  return `packages/${packageId}/tariff/`;
}

export function getTaxesURL(): string {
  return 'tax-list/';
}

export function assignTaxURL(packageId: number): string {
  return `packages/${packageId}/tax/`;
}

export const freezePackageURL = (packageId: number): string => {
  return `packages/${packageId}/freeze/`;
};